import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, memo } from 'react';
import { FixedSizeList } from 'react-window';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { Tag } from 'primereact/tag';
import ImageComponent from 'shared/ImageComponent';
import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import { UserDetailsContext } from 'context/userDetailsContext';
import '../mailboxV3.scss';

/* ── Constants ─────────────────────────────────────────────────────────── */
/** Fixed row height for react-window — matches MailList.jsx–style four-line layout */
const ROW_HEIGHT = 132;

/** Map API / snake_case statuses to legacy keys used by MailList.jsx Tag + getStatusClass */
const MAPPING_TO_LEGACY_STATUS = {
  mapped_by_ai: 'SUCCESS',
  unmapped_by_ai: 'NO_MATCH_FOUND',
  ai_mapping_failed: 'FAILED',
  mapped_by_user: 'MANUAL_OVERRIDE',
  failed_mail_deliveries: 'FAILED_MAIL',
  lawft_origin: 'LAWFT_ORIGIN',
  originated_in_lawft: 'LAWFT_ORIGIN',
};

/** Same labels as `MailList.jsx` statusMapping (PrimeReact Tag value) */
const LEGACY_STATUS_LABELS = {
  SUCCESS: 'Mapped By AI',
  BYPASSED: 'Mapping Bypassed',
  FAILED: 'AI Mapping Failed',
  MANUAL_OVERRIDE: 'Mapped By User',
  NO_MATCH_FOUND: 'Unmapped By AI',
  IN_PROGRESS: 'Mapping In Progress',
  QUEUED: 'Mapping Scheduled',
  LAWFT_ORIGIN: 'Originated in Lawft',
  FAILED_MAIL: 'Failed Mail Deliveries',
};

const getLegacyStatusKey = (thread) => {
  if (thread.failed_delivery) return 'FAILED_MAIL';
  if (thread.lawft_origin) return 'LAWFT_ORIGIN';
  const ms = thread.mapping_status;
  if (ms && MAPPING_TO_LEGACY_STATUS[ms]) return MAPPING_TO_LEGACY_STATUS[ms];
  if (thread.ai_reconciliation_status) return thread.ai_reconciliation_status;
  if (typeof ms === 'string' && /^[A-Z_]+$/.test(ms)) return ms;
  return 'NO_MATCH_FOUND';
};

const getMailListStatusClass = (status) => {
  if (status === 'SUCCESS') return 's-tag';
  if (status === 'FAILED' || status === 'FAILED_MAIL') return 'da-tag';
  if (status === 'IN_PROGRESS') return 'b-tag';
  return 'y-tag';
};

/* ── Pure helpers — defined outside component to prevent redefinition ─── */
const getSenderDisplayName = (thread) => thread?.email_from?.user_name || thread?.email_from?.user_email || thread?.from_email || '';

/** Resolves To line for firm list (V2 `to_email[]` or V1-style `email_members`). */
const getRecipientDisplayName = (thread) => {
  const toList = thread?.to_email?.length
    ? thread.to_email
    : (thread?.email_members?.filter((u) => !u.is_sender) ?? []).map((u) => u.user_name || u.user_email).filter(Boolean);
  if (!toList?.length) return '';
  const first = toList[0];
  if (toList.length === 1) return first;
  return `${first} +${toList.length - 1}`;
};

const getSenderAvatarLetter = (thread) => (thread?.email_from?.user_email || thread?.from_email || '?').charAt(0).toUpperCase();

const getRecipientAvatarLetter = (thread) => {
  const firstTo = thread?.to_email?.[0];
  if (firstTo) return firstTo.charAt(0).toUpperCase();
  const member = thread?.email_members?.find((u) => !u.is_sender);
  const email = member?.user_email || member?.user_name;
  return (email || '?').charAt(0).toUpperCase();
};

const formatTime = (dateStr, timezone) => {
  if (!dateStr) return '';
  return convertDateFromApiToUserTimezone(dateStr, timezone, 'hh:mm A');
};

const formatDate = (dateStr, timezone) => {
  if (!dateStr) return '';
  return convertDateFromApiToUserTimezone(dateStr, timezone, 'MM-DD-YYYY');
};

/* ── Skeleton row ────────────────────────────────────────────────────── */
const SkeletonRow = memo(() => (
  <div className="mail-v3-skeleton-row mail-v3-skeleton-row--mail-list">
    <div className="mail-v3-skeleton-av" />
    <div className="mail-v3-skeleton-body">
      <div className="mail-v3-skeleton-top">
        <div className="mail-v3-skeleton-line" style={{ width: '42%', height: '13px' }} />
        <div className="mail-v3-skeleton-line" style={{ width: '22%', height: '22px', borderRadius: '10px' }} />
      </div>
      <div className="mail-v3-skeleton-line mail-v3-skeleton-line--indented" style={{ width: '78%', height: '11px' }} />
      <div className="mail-v3-skeleton-line mail-v3-skeleton-line--indented" style={{ width: '55%', height: '11px' }} />
      <div className="mail-v3-skeleton-top mail-v3-skeleton-line--indented">
        <div className="mail-v3-skeleton-line" style={{ width: '26%', height: '10px' }} />
        <div className="mail-v3-skeleton-line" style={{ width: '18%', height: '10px' }} />
      </div>
    </div>
  </div>
));
SkeletonRow.displayName = 'SkeletonRow';

/* ── Virtualised row ─────────────────────────────────────────────────── */
/**
 * Rendered by react-window's FixedSizeList.
 * Receives all callbacks via itemData to keep the reference stable
 * and avoid re-creating the component on every parent render.
 */
const ThreadRow = memo(({ index, style, data }) => {
  const {
    threads,
    selectedThreadPk,
    readEmails,
    multiple,
    selectedEmails,
    timezone,
    handleThreadClick,
    toggleSelection,
    entireFirmLayout,
    isSentView,
  } = data;

  const thread = threads[index];
  if (!thread) return null;

  const isUnread = thread.is_unread && !readEmails[thread.pk];
  const isChecked = selectedEmails.some((e) => e.pk === thread.pk && e.sk === thread.sk);
  const isSelected = selectedThreadPk === thread.pk;
  const mailDate = thread.mail_date || thread.time;
  const legacyStatusKey = getLegacyStatusKey(thread);
  /** Label key matches `MailList.jsx` Tag value branch */
  const statusLabelKey = thread.failed_delivery
    ? 'FAILED_MAIL'
    : thread.lawft_origin
    ? 'LAWFT_ORIGIN'
    : thread.ai_reconciliation_status || legacyStatusKey;
  const statusLabel = LEGACY_STATUS_LABELS[statusLabelKey] || thread.ai_reconciliation_status || legacyStatusKey;
  /** Class key matches `MailList.jsx` getStatusClass (failed vs ai status only) */
  const statusClassKey = thread.failed_delivery ? 'FAILED_MAIL' : thread.ai_reconciliation_status || legacyStatusKey;
  const showStatusTag = thread.ai_reconciliation_status || thread.failed_delivery || thread.lawft_origin || thread.mapping_status;

  const rowBg = isSelected ? 'var(--selected-msg)' : 'var(--white)';

  const fromLabel = getSenderDisplayName(thread);
  const toLabel = getRecipientDisplayName(thread);
  const showFirmFromTo = entireFirmLayout && (fromLabel || toLabel);
  /** Inbox: From → To; Sent: To → From */
  const primaryLabel = showFirmFromTo ? (isSentView ? toLabel || '—' : fromLabel || '—') : fromLabel;
  const secondaryLabel = showFirmFromTo ? (isSentView ? fromLabel || '—' : toLabel || '—') : null;
  const avatarLetter = entireFirmLayout && isSentView ? getRecipientAvatarLetter(thread) : getSenderAvatarLetter(thread);

  return (
    <div style={style} className="mail-v3-rw-root">
      <div
        className={`mail-list-outer-box inner-border-bottom d-flex align-items-stretch mail-v3-mail-list-row${
          isUnread ? ' mail-v3-mail-list-row--unread' : ''
        }${isSelected ? ' mail-v3-mail-list-row--selected' : ''}`}
        style={{ backgroundColor: rowBg }}
      >
        <button
          type="button"
          className="flex-grow-1 p-2 pointer mail-list-box border-0 text-start mail-v3-mail-list-btn"
          style={{ backgroundColor: rowBg }}
          onClick={() => handleThreadClick(thread)}
        >
          <div className="d-flex flex-column gap-1 w-100 min-w-0">
            <div className="d-flex align-items-center gap-2 justify-content-between min-w-0 w-100">
              <div className="d-flex align-items-center gap-2 flex-grow-1 min-w-0">
                {multiple && (
                  <div className="d-flex align-items-center flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleSelection(thread.pk, thread.sk, thread.thread_id);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Select email: ${thread.subject || ''}`}
                    />
                  </div>
                )}
                <div className="position-relative flex-shrink-0">
                  <ImageComponent fileName={avatarLetter} style={{ backgroundColor: '#2196F3', color: '#ffffff' }} fileSize="medium.jpg" />
                  {isUnread && <i className="icon-window-filled mail-list-badge" aria-hidden="true" />}
                </div>
                {showFirmFromTo ? (
                  <div className={`d-flex align-items-center gap-1 min-w-0 flex-grow-1 ${isUnread ? 'unread-email' : ''}`}>
                    <h6 className="text-truncate black-700 mb-0 min-w-0">{primaryLabel}</h6>
                    <span className="flex-shrink-0 black-600" style={{ fontSize: '0.75rem' }} aria-hidden="true">
                      →
                    </span>
                    <h6 className="text-truncate black-700 mb-0 min-w-0">{secondaryLabel}</h6>
                  </div>
                ) : (
                  <h6 className={`text-truncate black-700 mb-0 ${isUnread ? 'unread-email' : ''}`}>{fromLabel}</h6>
                )}
              </div>

              {showStatusTag && (
                <div className="flex-shrink-0">
                  <Tag
                    className={`tag-case mail-ai-tags text-center d-status-tags d-flex ${getMailListStatusClass(statusClassKey)}`}
                    rounded
                    value={
                      <>
                        <i className="bi bi-stars me-2" aria-hidden="true" />
                        {statusLabel}
                      </>
                    }
                  />
                </div>
              )}
            </div>

            <div className={`${isUnread ? 'unread-email' : ''} ms-5 ps-3`}>
              <span className={`pointer text-truncate d-block black-700 ${isUnread ? 'text-bold' : ''}`}>
                {thread.subject || '(No Subject)'}
              </span>
            </div>

            {thread.case_id && (
              <div className={`${isUnread ? 'unread-email' : ''} ms-5 ps-3`}>
                <span className="text-truncate black-700 mb-2 mt-2 d-block">
                  Case/Lead:
                  <Link
                    to={`${thread.is_lead ? '/leads' : '/cases'}/${thread.case_id}/mails`}
                    className={`redirection-link-no-text-transform ellipsis-text case-name-tooltip-${thread.index}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {' '}
                    {thread.case_name}
                  </Link>
                </span>
                <Tooltip target={`.case-name-tooltip-${thread.index}`} className="case-tooltip" content={thread.case_name} position="top" />
              </div>
            )}

            <div className="d-flex gap-2 ms-5 ps-3 mt-1 justify-content-between">
              <span className="text-hint black-600 m-0">{formatDate(mailDate, timezone)}</span>
              <span className="text-hint black-600 m-0">{formatTime(mailDate, timezone)}</span>
            </div>
          </div>
        </button>
      </div>
    </div>
  );
});
ThreadRow.displayName = 'ThreadRow';

/* ── Main component ──────────────────────────────────────────────────── */
const MailListV3 = ({
  threads,
  totalCount,
  selectedThreadPk,
  setSelectedThreadPkSk,
  handleLoadMore,
  loading,
  hasMore,
  paginationLoading,
  readEmails,
  setReadEmails,
  multiple,
  selectedEmails,
  setSelectedEmails,
  isUnreadTab,
  markRead,
  markUnread,
  onBulkMapToCase,
  entireFirmLayout,
  isSentView,
}) => {
  const userContext = useContext(UserDetailsContext);
  const listRef = useRef(null);
  const containerRef = useRef(null);
  const [selectAll, setSelectAll] = useState(false);
  const [containerHeight, setContainerHeight] = useState(500);
  const timezone = userContext?.userDetails?.timezone;

  /* ── Measure the container so FixedSizeList fills it exactly ──────── */
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setContainerHeight(entry.contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  /* ── Select-all sync ──────────────────────────────────────────────── */
  useEffect(() => {
    if (!multiple || !threads?.length) {
      setSelectAll(false);
      return;
    }
    const allSelected = threads.every((t) => selectedEmails.some((e) => e.pk === t.pk && e.sk === t.sk));
    setSelectAll(allSelected);
  }, [multiple, threads, selectedEmails]);

  /* ── Stable callbacks ─────────────────────────────────────────────── */
  const handleSelectAllChange = useCallback(
    (e) => {
      e.stopPropagation();
      const isChecked = e.target.checked;
      setSelectedEmails(isChecked ? threads.map((t) => ({ pk: t.pk, sk: t.sk, thread_id: t.thread_id })) : []);
      setSelectAll(isChecked);
    },
    [threads, setSelectedEmails]
  );

  const toggleSelection = useCallback(
    (pk, sk, thread_id) => {
      setSelectedEmails((prev) => {
        const exists = prev.some((e) => e.pk === pk && e.sk === sk);
        if (exists) return prev.filter((e) => !(e.pk === pk && e.sk === sk));
        return [...prev, { pk, sk, thread_id }];
      });
    },
    [setSelectedEmails]
  );

  const handleThreadClick = useCallback(
    (thread) => {
      setSelectedThreadPkSk({ pk: thread.pk, sk: thread.sk });
      setReadEmails((prev) => ({ ...prev, [thread.pk]: true }));
    },
    [setSelectedThreadPkSk, setReadEmails]
  );

  /** Same rule as ReconcileHeader: if every selected thread is effectively read, primary action is Mark as unread. */
  const { bulkMarkAsUnread, bulkReadUnreadLabel } = useMemo(() => {
    if (!selectedEmails?.length) {
      return { bulkMarkAsUnread: false, bulkReadUnreadLabel: 'Mark read' };
    }
    const localRead = readEmails ?? {};
    const allEffectivelyRead = selectedEmails.every((email) => {
      const thread = threads.find((t) => t.pk === email.pk && t.sk === email.sk);
      if (!thread) return false;
      const effectivelyUnread = Boolean(thread.is_unread) && !localRead[thread.pk];
      return !effectivelyUnread;
    });
    return {
      bulkMarkAsUnread: allEffectivelyRead,
      bulkReadUnreadLabel: allEffectivelyRead ? 'Mark as unread' : 'Mark read',
    };
  }, [threads, readEmails, selectedEmails]);

  const handleBulkReadUnread = useCallback(() => {
    if (!markRead || !markUnread) return;
    const threadIds = selectedEmails
      .map((e) => {
        const thread = threads.find((t) => t.pk === e.pk && t.sk === e.sk);
        return thread?.thread_id ?? thread?.email_thread_id ?? e.thread_id;
      })
      .filter(Boolean);
    if (!threadIds.length) return;

    if (bulkMarkAsUnread) {
      markUnread(threadIds);
      setReadEmails((prev) => {
        const next = { ...prev };
        selectedEmails.forEach((e) => {
          delete next[e.pk];
        });
        return next;
      });
    } else {
      markRead(threadIds);
      setReadEmails((prev) => {
        const next = { ...prev };
        selectedEmails.forEach((e) => {
          next[e.pk] = true;
        });
        return next;
      });
    }
  }, [markRead, markUnread, threads, selectedEmails, bulkMarkAsUnread, setReadEmails]);

  /* ── Infinite scroll trigger via react-window's onItemsRendered ───── */
  const handleItemsRendered = useCallback(
    ({ visibleStopIndex }) => {
      if (!hasMore || paginationLoading) return;
      if (visibleStopIndex >= threads.length - 3) {
        handleLoadMore();
      }
    },
    [hasMore, paginationLoading, threads.length, handleLoadMore]
  );

  /* ── Stable itemData — prevents unnecessary row re-renders ─────────── */
  const itemData = useMemo(
    () => ({
      threads,
      selectedThreadPk,
      readEmails,
      multiple,
      selectedEmails,
      timezone,
      handleThreadClick,
      toggleSelection,
      entireFirmLayout,
      isSentView,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [threads, selectedThreadPk, readEmails, multiple, selectedEmails, timezone, entireFirmLayout, isSentView]
  );

  return (
    <>
      {multiple && threads?.length > 0 && (
        <div className="mail-v3-select-all-row">
          <input
            type="checkbox"
            id="mailListV3SelectAll"
            checked={selectAll}
            onChange={handleSelectAllChange}
            aria-label={isUnreadTab ? 'Select all unread emails' : 'Select all emails'}
          />
          <label htmlFor="mailListV3SelectAll" className="mb-0 pointer" style={{ fontSize: '12.5px' }}>
            {selectedEmails.length > 0
              ? `${selectedEmails.length} selected · ${totalCount ?? threads.length} total`
              : `Select all · ${threads.length} shown of ${totalCount ?? threads.length}`}
          </label>
        </div>
      )}

      {multiple && selectedEmails.length > 0 && (typeof markRead === 'function' || typeof onBulkMapToCase === 'function') && (
        <div className="mail-v3-bulk-bar">
          <span>{selectedEmails.length} selected</span>
          {typeof markRead === 'function' && typeof markUnread === 'function' && (
            <Button
              type="button"
              label={bulkReadUnreadLabel}
              className="p-button-sm btn-outline p-button-secondary"
              onClick={handleBulkReadUnread}
            />
          )}
          {typeof onBulkMapToCase === 'function' && (
            <Button
              type="button"
              label="Map to Case/Lead"
              className="p-button-sm btn-outline p-button-secondary"
              onClick={onBulkMapToCase}
            />
          )}
        </div>
      )}

      <div className="mail-v3-email-list" ref={containerRef}>
        {loading && (
          <>
            {Array.from({ length: 7 }).map((_, i) => (
              <SkeletonRow key={i} />
            ))}
          </>
        )}

        {!loading && (!threads || threads.length === 0) && <div className="mail-v3-list-empty">No emails match your filters.</div>}

        {!loading && threads.length > 0 && (
          <FixedSizeList
            ref={listRef}
            height={containerHeight}
            itemCount={threads.length}
            itemSize={ROW_HEIGHT}
            itemData={itemData}
            onItemsRendered={handleItemsRendered}
            style={{ overflowX: 'hidden' }}
          >
            {ThreadRow}
          </FixedSizeList>
        )}

        {paginationLoading && (
          <div className="mail-v3-list-loader">
            <i className="pi pi-spin pi-spinner me-2" aria-hidden="true" />
            Loading more...
          </div>
        )}
      </div>
    </>
  );
};

MailListV3.propTypes = {
  totalCount: PropTypes.number,
  threads: PropTypes.arrayOf(
    PropTypes.shape({
      pk: PropTypes.string,
      sk: PropTypes.string,
      from_email: PropTypes.string,
      to_email: PropTypes.arrayOf(PropTypes.string),
      subject: PropTypes.string,
      mail_date: PropTypes.string,
      is_unread: PropTypes.bool,
      mapping_status: PropTypes.string,
      case_id: PropTypes.string,
      case_name: PropTypes.string,
      is_lead: PropTypes.bool,
    })
  ),
  selectedThreadPk: PropTypes.string,
  setSelectedThreadPkSk: PropTypes.func.isRequired,
  handleLoadMore: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  hasMore: PropTypes.bool,
  paginationLoading: PropTypes.bool,
  readEmails: PropTypes.object,
  setReadEmails: PropTypes.func,
  multiple: PropTypes.bool,
  selectedEmails: PropTypes.array,
  setSelectedEmails: PropTypes.func,
  isUnreadTab: PropTypes.bool,
  markRead: PropTypes.func,
  markUnread: PropTypes.func,
  onBulkMapToCase: PropTypes.func,
  /** When true (Entire Firm), list row shows From/To per folder rules. */
  entireFirmLayout: PropTypes.bool,
  /** Inbox vs Sent — only used with entireFirmLayout. */
  isSentView: PropTypes.bool,
};

MailListV3.defaultProps = {
  totalCount: null,
  threads: [],
  selectedThreadPk: null,
  loading: false,
  hasMore: false,
  paginationLoading: false,
  readEmails: {},
  setReadEmails: () => {},
  multiple: false,
  selectedEmails: [],
  setSelectedEmails: () => {},
  isUnreadTab: false,
  markRead: null,
  markUnread: null,
  onBulkMapToCase: null,
  entireFirmLayout: false,
  isSentView: false,
};

export default MailListV3;
